import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import FulfillmentSelectorModal from '../FulfillmentSelectorModal/FulfillmentSelectorModal'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import { useAvailability } from '../../utils/availability'
import { useOutpostRestaurant } from '../OutpostProvider/OutpostProvider'
import { FulfillmentDisplay } from '../FulfillmentDisplay/FulfillmentDisplay'
import { useDiningOptions, DINING_OPTION_BEHAVIORS } from '../../utils/dining-options'

import './FulfillmentSelector.css'
import { useFlag } from '../FeatureFlag/use-flag'

const { DELIVERY } = DINING_OPTION_BEHAVIORS

// Fulfillment display bar (which will open modal upon click as well as prior to having fulfillment selected e.g. on first menu load)
export const FulfillmentSelector = ({ minimumTakeoutTime }) => {
  const [state, setState] = useState({
    isModalShowing: false,
    enabled: false
  })
  const deliveryEnabled = useFlag('oo-v3-delivery')
  const shouldDefaultToTakeOutAsap = useFlag('oo-v3-default-take-out-asap')
  const { loading: availabilityLoading, futureDatesAvailable } = useAvailability()
  const { selected, loading: fulfillmentLoading, valid } = useFulfillment()
  const { isOutpost, outpostName, outpostDeliveryTime } = useOutpostRestaurant()
  const { loading: diningOptionsLoading, data: diningOptionsData } = useDiningOptions({ networkOnly: false })
  const loading = diningOptionsLoading || availabilityLoading || fulfillmentLoading

  // Because we're dealing with an object, it can't simply be a dependent of
  // the effect. We must derrive the data we need from it.
  const hasDelivery = useMemo(
    () => {
      const { diningOptions = [] } = diningOptionsData || {}
      return !loading && deliveryEnabled &&
        diningOptions
          .some(({ behavior }) => behavior === DELIVERY)
    },
    [deliveryEnabled, diningOptionsData, loading]
  )

  useEffect(() => {
    if (!loading) {
      const enabled = Boolean(
        (hasDelivery || futureDatesAvailable) && !isOutpost
      )

      const isModalShowing = (
        !shouldDefaultToTakeOutAsap &&
        (!selected || !valid) &&
        enabled
      )

      setState({
        isModalShowing,
        enabled
      })
    }
  }, [
    hasDelivery,
    valid,
    selected,
    loading,
    isOutpost,
    futureDatesAvailable,
    shouldDefaultToTakeOutAsap
  ])

  const setModal = useCallback(isModalShowing => {
    setState(prevState => ({ ...prevState, isModalShowing }))
  }, [])
  const showModal = useCallback(() => setModal(true), [setModal])
  const hideModal = useCallback(() => setModal(false), [setModal])

  return (
    <React.Fragment>
      <button
        styleName={cx('fulfillmentSelector', { pointer: state.enabled })}
        id='editFulfillment'
        data-testid='edit-fulfillment'
        disabled={!state.enabled}
        onClick={showModal}
        type='button'
      >
        <FulfillmentDisplay
          fulfillmentNeedsSelecting={state.isModalShowing}
          editable={state.enabled}
          outpostName={outpostName}
          outpostDeliveryTime={outpostDeliveryTime}
          centered
          inline
        />
      </button>
      {state.isModalShowing && (
        <FulfillmentSelectorModal
          minimumTakeoutTime={minimumTakeoutTime}
          onClose={hideModal}
        />
      )}
    </React.Fragment>
  )
}

FulfillmentSelector.propTypes = {
  minimumTakeoutTime: PropTypes.number.isRequired
}
